import { supabase, supabaseAdmin } from './supabase';
import type { Database } from './database.types';

type Tables = Database['public']['Tables'];

// Helper class for database operations
class DatabaseHelper {
  // Dashboard Overview
  async getDashboardOverview() {
    try {
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('role');

      if (usersError) throw usersError;

      const totalRiders = users?.filter((u) => u.role === 'rider').length || 0;
      const totalDrivers = users?.filter((u) => u.role === 'driver').length || 0;
      const totalSupportWorkers =
        users?.filter((u) => u.role === 'support_worker').length || 0;

      const { count: totalBookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true });

      if (bookingsError) throw bookingsError;

      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const { count: newBookings, error: newBookingsError } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', yesterday.toISOString());

      if (newBookingsError) throw newBookingsError;

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: paidThisMonth, error: revenueError } = await supabase
        .from('bookings')
        .select('fare_estimate, stripe_amount_paid, payment_status')
        .gte('created_at', startOfMonth.toISOString())
        .eq('payment_status', 'paid');

      if (revenueError) throw revenueError;

      const monthRevenue =
        paidThisMonth?.reduce((sum, b) => {
          const fare = Number(b.fare_estimate) || 0;
          const paid = Number(b.stripe_amount_paid);
          return sum + (Number.isFinite(paid) && paid > 0 ? paid : fare);
        }, 0) || 0;

      const { count: activeTrips, error: activeError } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'in_progress');

      if (activeError) throw activeError;

      let pendingIntakes = 0;
      const { count: pendingDrivers, error: pdErr } = await supabase
        .from('driver_intake_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      const { count: pendingSw, error: psErr } = await supabase
        .from('support_worker_intake_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (!pdErr && !psErr) {
        pendingIntakes = (pendingDrivers || 0) + (pendingSw || 0);
      }

      return {
        data: {
          total_riders: totalRiders,
          total_drivers: totalDrivers,
          total_support_workers: totalSupportWorkers,
          total_bookings: totalBookings || 0,
          new_bookings: newBookings || 0,
          month_bookings: paidThisMonth?.length || 0,
          month_revenue: monthRevenue,
          active_trips: activeTrips || 0,
          pending_applications: pendingIntakes,
        },
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  // Get all users with pagination and filters
  async getAllUsers(page = 1, limit = 50, filters?: { role?: string; search?: string }) {
    try {
      let query = supabase
        .from('users')
        .select(`
          *,
          profiles (
            name,
            phone,
            address,
            is_verified,
            is_active,
            created_at
          )
        `);

      // Apply filters
      if (filters?.role) {
        query = query.eq('role', filters.role);
      }

      if (filters?.search) {
        query = query.or(`email.ilike.%${filters.search}%,profiles.name.ilike.%${filters.search}%`);
      }

      // Get total count
      const { count, error: countError } = await query.select('*', { count: 'exact', head: true });
      if (countError) throw countError;

      // Get paginated data
      const { data, error } = await query
        .range((page - 1) * limit, page * limit - 1)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: data || [],
        pagination: {
          page,
          limit,
          total: count || 0,
          pages: Math.ceil((count || 0) / limit)
        },
        error: null
      };
    } catch (error) {
      return {
        data: [],
        pagination: { page: 1, limit, total: 0, pages: 0 },
        error: error instanceof Error ? error : new Error('Unknown error')
      };
    }
  }

  /**
   * Supabase V1: single `bookings` table (guest + card). Admin RLS required.
   */
  async getAllBookings(
    page = 1,
    limit = 50,
    filters?: {
      status?: string;
      dateFrom?: string;
      dateTo?: string;
      paymentMethod?: string;
      paymentStatus?: string;
    },
  ) {
    try {
      let query = supabase
        .from('bookings')
        .select('*', { count: 'exact' });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      if (filters?.paymentMethod) {
        query = query.eq('payment_method', filters.paymentMethod);
      }

      if (filters?.paymentStatus) {
        query = query.eq('payment_status', filters.paymentStatus);
      }

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range((page - 1) * limit, page * limit - 1);

      if (error) throw error;

      const rows = (data || []).map((row: Record<string, unknown>) => {
        const extras = (row.extras as Record<string, unknown>) || {};
        const sw = extras.support_workers_count;
        const vf = extras.vehicle_features;
        return {
          id: row.id as string,
          booking_type: 'guest' as const,
          customer_name: (row.customer_name as string) ?? null,
          customer_email: row.customer_email as string,
          customer_phone: (row.customer_phone as string) ?? null,
          pickup_address: row.pickup_address as string,
          dropoff_address: row.dropoff_address as string,
          pickup_time: row.pickup_time as string,
          status: row.status as string,
          fare_estimate: Number(row.fare_estimate) || 0,
          support_workers_count:
            typeof sw === 'number' ? sw : Number(sw) || 0,
          created_at: row.created_at as string | null,
          vehicle_features: Array.isArray(vf) ? (vf as string[]) : [],
          special_requirements:
            (extras.special_requirements as string) || null,
          payment_method: row.payment_method as 'cash_bank' | 'stripe',
          payment_status: row.payment_status as string,
          payment_confirmed_at: null as string | null,
          stripe_payment_intent_id: row.stripe_payment_intent_id as string | null,
          stripe_amount_paid:
            row.stripe_amount_paid != null
              ? Number(row.stripe_amount_paid)
              : null,
        };
      });

      const total = count ?? 0;

      return {
        data: rows,
        pagination: {
          page,
          limit,
          total,
          pages: Math.max(1, Math.ceil(total / limit)),
        },
        error: null,
      };
    } catch (error) {
      return {
        data: [],
        pagination: { page: 1, limit, total: 0, pages: 0 },
        error: error instanceof Error ? error : new Error('Unknown error')
      };
    }
  }

  /** Pre-account driver intake (V1 partner flow; no auth.users link). */
  async getDriverApplications(page = 1, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('driver_intake_submissions')
        .select('*')
        .order('created_at', { ascending: false })
        .range((page - 1) * limit, page * limit - 1);

      if (error) throw error;

      const { count } = await supabase
        .from('driver_intake_submissions')
        .select('*', { count: 'exact', head: true });

      return {
        data: data || [],
        pagination: {
          page,
          limit,
          total: count || 0,
          pages: Math.ceil((count || 0) / limit)
        },
        error: null
      };
    } catch (error) {
      return {
        data: [],
        pagination: { page: 1, limit, total: 0, pages: 0 },
        error: error instanceof Error ? error : new Error('Unknown error')
      };
    }
  }

  /** Pre-account support worker intake (V1 partner flow). */
  async getSupportWorkerApplications(page = 1, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('support_worker_intake_submissions')
        .select('*')
        .order('created_at', { ascending: false })
        .range((page - 1) * limit, page * limit - 1);

      if (error) throw error;

      const { count } = await supabase
        .from('support_worker_intake_submissions')
        .select('*', { count: 'exact', head: true });

      return {
        data: data || [],
        pagination: {
          page,
          limit,
          total: count || 0,
          pages: Math.ceil((count || 0) / limit)
        },
        error: null
      };
    } catch (error) {
      return {
        data: [],
        pagination: { page: 1, limit, total: 0, pages: 0 },
        error: error instanceof Error ? error : new Error('Unknown error')
      };
    }
  }

  // Get vehicle registrations
  async getVehicleRegistrations(page = 1, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('vehicle_registrations')
        .select(`
          *,
          profiles!vehicle_registrations_user_id_fkey (
            name,
            email,
            phone
          )
        `)
        .order('created_at', { ascending: false })
        .range((page - 1) * limit, page * limit - 1);

      if (error) throw error;

      const { count } = await supabase
        .from('vehicle_registrations')
        .select('*', { count: 'exact', head: true });

      return {
        data: data || [],
        pagination: {
          page,
          limit,
          total: count || 0,
          pages: Math.ceil((count || 0) / limit)
        },
        error: null
      };
    } catch (error) {
      return {
        data: [],
        pagination: { page: 1, limit, total: 0, pages: 0 },
        error: error instanceof Error ? error : new Error('Unknown error')
      };
    }
  }

  // Promote user to admin
  async promoteUserToAdmin(userId: string) {
    try {
      const { data, error } = await supabaseAdmin
        .from('users')
        .update({ role: 'admin' })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error')
      };
    }
  }

  // Verify vehicle
  async verifyVehicle(vehicleId: string, verified: boolean, notes?: string) {
    try {
      const { data, error } = await supabase
        .from('vehicle_registrations')
        .update({
          is_verified: verified,
          verification_notes: notes,
          verified_at: verified ? new Date().toISOString() : null
        })
        .eq('id', vehicleId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error')
      };
    }
  }

  // Verify support worker
  async verifySupportWorker(supportWorkerId: string, verified: boolean, notes?: string) {
    try {
      const { data, error } = await supabase
        .from('support_worker_applications')
        .update({
          is_verified: verified,
          verification_notes: notes,
          verified_at: verified ? new Date().toISOString() : null
        })
        .eq('id', supportWorkerId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error')
      };
    }
  }
}

// Export singleton instance
export const db = new DatabaseHelper();
